import { Button } from "@dash/form";
import { Dialog } from "@dash/dialog";
import { useTheme } from "@emotion/react";
import React from "react";
import { MdMerge } from "react-icons/md";
import { useNavigate } from "react-router-dom-v5-compat";
import { useQueryClient } from "react-query";

import { useClientManager } from "@megaron/react-clients";
import { TicketDetailDto, TicketListItemDto } from "@megaron/support-contracts";

type Props = {
  currentTicket: TicketDetailDto;
  targetTicket: TicketListItemDto;
  listPath: string;
  onClose: () => void;
};

export const SupportTicketMergeDialog: React.FC<Props> = ({ currentTicket, targetTicket, listPath, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const support = useClientManager("support");

  const mergeMutation = support.mergeTickets().useMutation({
    onSuccess: (mergedTicket) => {
      onClose();
      queryClient.invalidateQueries({ queryKey: ["support"] });
      navigate(`${listPath}/${mergedTicket.id}`);
    },
  });

  const handleConfirm = () => {
    mergeMutation.mutate({ id: currentTicket.id, targetTicketId: targetTicket.id });
  };

  const isLoading = "isLoading" in mergeMutation ? mergeMutation.isLoading : false;

  return (
    <Dialog
      header="Połącz zgłoszenia"
      onClose={onClose}
      footer={
        <div css={{ display: "flex", gap: "0.5rem", marginLeft: "auto" }}>
          <Button variant="outline" onClick={onClose} isDisabled={isLoading}>
            Anuluj
          </Button>
          <Button icon={<MdMerge />} onClick={handleConfirm} isLoading={isLoading}>
            Połącz
          </Button>
        </div>
      }
    >
      <div css={{ display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "28rem" }}>
        <p css={{ margin: 0, color: theme.colors.primary }}>
          Wiadomości i notatki z bieżącego zgłoszenia zostaną przeniesione do starszego wątku, a bieżące zgłoszenie zostanie usunięte.
        </p>
        <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div css={{ fontSize: "0.875rem", color: theme.colors.secondaryText }}>Bieżące zgłoszenie (zostanie usunięte)</div>
          <div
            css={{
              padding: "0.75rem",
              borderRadius: theme.smallBorderRadius,
              background: theme.colors.primaryLight,
              border: `1px solid ${theme.colors.primaryLight}`,
            }}
          >
            <div css={{ fontWeight: 600, fontSize: "0.9375rem" }}>{currentTicket.subject}</div>
            <div css={{ fontSize: "0.8125rem", color: theme.colors.secondaryText, marginTop: "0.25rem" }}>
              {currentTicket.reporterEmail}
            </div>
          </div>
        </div>
        <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <div css={{ fontSize: "0.875rem", color: theme.colors.secondaryText }}>Wątek docelowy</div>
          <div
            css={{
              padding: "0.75rem",
              borderRadius: theme.smallBorderRadius,
              background: theme.colors.primaryLight,
              border: `1px solid ${theme.colors.primaryLight}`,
            }}
          >
            <div css={{ fontWeight: 600, fontSize: "0.9375rem" }}>{targetTicket.subject}</div>
            <div css={{ fontSize: "0.8125rem", color: theme.colors.secondaryText, marginTop: "0.25rem" }}>
              {targetTicket.reporterEmail}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
